import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ApproversInfo } from "@/shared/components/ApproversInfo";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ReasonDialog } from "@/features/invoicing/components/ReasonDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Field, FieldLabel } from "@/components/ui/field";
import { useNotification } from "@/shared/hooks/useNotification";
import {
  useApproveReserveRelease,
  usePendingReserveReleases,
  useRejectReserveRelease,
} from "../hooks/useReserveRelease";
import type { GreenhouseStockReserveReleaseDto } from "../services/reserveReleaseApi";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ApproveModal = ({
  release,
  open,
  onOpenChange,
  onConfirm,
  isSubmitting,
}: {
  release: GreenhouseStockReserveReleaseDto | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConfirm: (notes: string) => void;
  isSubmitting: boolean;
}) => {
  const [notes, setNotes] = useState("");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve Reserve Release</DialogTitle>
          <DialogDescription>
            {release ? (
              <>
                Approving will move <strong>{release.quantityRequested}</strong> {release.stockUnit.toLowerCase()}
                {" "}from filling reserve to sellable stock on{" "}
                <strong>
                  {release.cropTypeName}
                  {release.variety ? ` — ${release.variety}` : ""}
                </strong>
                . Current filling reserve: {release.currentFillingReserve}.
              </>
            ) : null}
          </DialogDescription>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor="approvalNotes">Decision Notes (optional)</FieldLabel>
          <Textarea
            id="approvalNotes"
            rows={3}
            placeholder="Any notes for the requester..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
            maxLength={1000}
          />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(notes)} disabled={isSubmitting}>
            {isSubmitting ? "Approving..." : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ReserveReleaseApprovalsPage = () => {
  const notify = useNotification();
  const pendingQuery = usePendingReserveReleases();
  const approveMutation = useApproveReserveRelease();
  const rejectMutation = useRejectReserveRelease();

  const [selected, setSelected] = useState<GreenhouseStockReserveReleaseDto | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const openApprove = (item: GreenhouseStockReserveReleaseDto) => {
    setSelected(item);
    setApproveOpen(true);
  };

  const openReject = (item: GreenhouseStockReserveReleaseDto) => {
    setSelected(item);
    setRejectOpen(true);
  };

  const handleApprove = (notes: string) => {
    if (!selected) return;
    approveMutation.mutate(
      { id: selected.id, decisionComment: notes.trim() || undefined },
      {
        onSuccess: () => {
          notify.success("Reserve release approved — stock updated");
          setApproveOpen(false);
          setSelected(null);
        },
        onError: (err: any) =>
          notify.error(err?.response?.data?.message ?? "Failed to approve reserve release"),
      }
    );
  };

  const handleReject = (reason: string) => {
    if (!selected) return;
    rejectMutation.mutate(
      { id: selected.id, decisionComment: reason },
      {
        onSuccess: () => {
          notify.success("Reserve release rejected");
          setRejectOpen(false);
          setSelected(null);
        },
        onError: (err: any) =>
          notify.error(err?.response?.data?.message ?? "Failed to reject reserve release"),
      }
    );
  };

  const rows = pendingQuery.data ?? [];
  const isBusy = approveMutation.isPending || rejectMutation.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Reserve Release Approvals</h1>
        <p className="mt-1 text-muted-foreground">
          Review requests to release filling-reserve stock (30% backfill buffer) back to sellable
          inventory. Approval moves the requested quantity into available stock.
        </p>
        <div className="mt-2">
          <ApproversInfo permission="greenhouse_stock.approve_reserve_release" />
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <CardTitle>Pending Reserve Releases</CardTitle>
            {rows.length > 0 && (
              <Badge className="border-orange-400 bg-orange-100 text-orange-700">
                {rows.length}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {pendingQuery.isLoading && (
            <p className="py-8 text-center text-muted-foreground">Loading...</p>
          )}
          {!pendingQuery.isLoading && rows.length === 0 && (
            <p className="py-8 text-center text-muted-foreground">
              No pending reserve-release requests.
            </p>
          )}
          {rows.length > 0 && (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Crop / Variety</TableHead>
                    <TableHead>Tray Type</TableHead>
                    <TableHead>Qty Requested</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Current Reserve</TableHead>
                    <TableHead>Current Total</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Requested By</TableHead>
                    <TableHead>Requested At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-sm">
                        {item.cropTypeName}
                        {item.variety ? ` — ${item.variety}` : ""}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.trayType ? <Badge variant="outline">{item.trayType}</Badge> : "—"}
                      </TableCell>
                      <TableCell className="font-semibold">{item.quantityRequested}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.stockUnit}</TableCell>
                      <TableCell className="text-sm">{item.currentFillingReserve}</TableCell>
                      <TableCell className="text-sm">{item.currentTotalQuantity}</TableCell>
                      <TableCell className="max-w-xs text-sm text-muted-foreground truncate" title={item.reason}>
                        {item.reason}
                      </TableCell>
                      <TableCell className="text-sm">{item.requestedByUserName}</TableCell>
                      <TableCell className="text-sm">{formatDate(item.requestedAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => openApprove(item)}
                            disabled={isBusy}
                          >
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => openReject(item)}
                            disabled={isBusy}
                          >
                            Reject
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ApproveModal
        release={selected}
        open={approveOpen}
        onOpenChange={setApproveOpen}
        onConfirm={handleApprove}
        isSubmitting={approveMutation.isPending}
      />

      <ReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="Reject Reserve Release"
        description="Provide a reason. The requester will see this message."
        reasonLabel="Rejection Reason"
        reasonPlaceholder="e.g. Disease risk not yet cleared — please re-evaluate next week"
        submitLabel="Reject"
        isSubmitting={rejectMutation.isPending}
        onSubmit={handleReject}
      />
    </div>
  );
};
